/**
 * WP-12 title screen + main menu. The front door that ties the four play modes
 * together (previously reachable only by URL param). A moonlit-poster hero —
 * hand-authored inline SVG (full moon over a teal moonlit sea, a dark-red
 * battleship silhouette, framed like the original press art) — over a parchment
 * menu. Riffs on the LAR poster composition with original art (trademark-safe).
 *
 * Pure presentation: it owns no game state, just calls the navigation callbacks.
 * Leaving fades the poster out (a soft transition); the still itself is static so
 * its golden is deterministic.
 */
import { DIFFICULTY_NAMES } from '../rules/ai';
import { hasSave, savedMeta } from './save';
import { fitScreen } from './screen';
import { APP_VERSION } from '../version';
import { ICON_ANCHOR, ICON_SWORDS, ICON_MAP, ICON_ZAP, ICON_CAP, ICON_GEAR, ICON_SCROLL } from './icons';

const CSS = `
.ar-title { box-sizing:border-box; display:flex; flex-direction:column; width: 100%; min-width: 0; height: 100dvh; transform-origin: top center; container-type: inline-size; margin: 0 auto; font-family: Georgia, 'Times New Roman', serif; color: hsl(28, 55.6%, 10.6%);
  --navy:hsl(217, 53.7%, 16.1%); --parch:hsl(44, 53.7%, 83.9%); --parch2:hsl(40, 46.9%, 71.2%); --wood:hsl(31, 47.6%, 28.4%); --wood-d:hsl(30, 54.2%, 18.8%);
  --brass:hsl(43, 63.2%, 58.4%); --brass-l:hsl(46, 80.7%, 73.5%); --brass-d:hsl(42, 61.6%, 34.7%); --head:hsl(23, 66.7%, 21.2%);
  --sv:hsl(214, 16.3%, 83.1%); --sv-d:hsl(219, 5.5%, 49.8%);
  opacity: 1; transition: opacity 0.22s ease; }
.ar-title.leaving { opacity: 0; }
/* ornate silver frame INSIDE the poster: a thin double line + scroll flourishes
   at the four corners (one corner SVG, mirrored). Frames the scene, not the card. */
.ar-title .ar-frame { position:absolute; inset:16px; z-index:3; pointer-events:none;
  border:1.6px solid var(--sv); outline:1px solid hsla(214, 16.3%, 83.1%, 0.3); outline-offset:4px;
  box-shadow: 0 1px 4px hsla(0, 0%, 0%, 0.4); }
.ar-title .ar-corner { position:absolute; width:62px; height:62px; filter: drop-shadow(0 1px 1px hsla(0, 0%, 0%, 0.5)); }
.ar-title .ar-corner svg { width:100%; height:100%; display:block; }
.ar-title .ar-corner.tl { top:-11px; left:-11px; }
.ar-title .ar-corner.tr { top:-11px; right:-11px; transform:scaleX(-1); }
.ar-title .ar-corner.bl { bottom:-11px; left:-11px; transform:scaleY(-1); }
.ar-title .ar-corner.br { bottom:-11px; right:-11px; transform:scale(-1,-1); }
/* the poster is a flexible hero: it grows/shrinks with the available height while the
   SVG art covers it (slice). Base = aspect 1.8 (natural art); the aspect is clamped
   between 0.6 (tallest — max-height 166.67cqw) and 4 (shortest strip — min-height 25cqw). */
.ar-title .hero { position:relative; flex:100 1 55.56cqw; min-height:25cqw; max-height:166.67cqw; overflow:hidden;
  container-type:inline-size; border:3px solid var(--wood); border-bottom:none; box-sizing:border-box; }
.ar-title .poster { position:absolute; inset:0; display:block; width:100%; height:100%; }
/* subtle ambient motion: stars blink like fireflies, moonlight glints shimmer */
/* several blinks per cycle at irregular offsets & depths, so each blink differs
   from the last — combined with each star's own duration/phase the field reads
   genuinely random rather than a synchronised metronome pulse. */
@keyframes ar-twinkle {
  0%   { opacity: calc(var(--o,0.7) * 0.85); }
  9%   { opacity: calc(var(--o,0.7) * 0.30); }
  18%  { opacity: var(--o,0.7); }
  29%  { opacity: calc(var(--o,0.7) * 0.45); }
  43%  { opacity: calc(var(--o,0.7) * 0.95); }
  54%  { opacity: calc(var(--o,0.7) * 0.20); }
  66%  { opacity: calc(var(--o,0.7) * 0.70); }
  79%  { opacity: calc(var(--o,0.7) * 0.35); }
  88%  { opacity: var(--o,0.7); }
  100% { opacity: calc(var(--o,0.7) * 0.85); }
}
/* glints drift sideways and flicker in brightness (opacity tied to each glint's
   own --ro) so the column shimmers like diffuse moonlight rather than sliding
   as one rigid block. */
@keyframes ar-shimmer {
  0%   { transform: translate(-2.5px, 0.4px); opacity: var(--ro,0.4);             }
  25%  { transform: translate(0.5px, -0.7px); opacity: calc(var(--ro,0.4) * 0.4); }
  50%  { transform: translate(2.5px, 0.4px);  opacity: var(--ro,0.4);             }
  75%  { transform: translate(-0.5px, 0.9px); opacity: calc(var(--ro,0.4) * 0.6); }
  100% { transform: translate(-2.5px, 0.4px); opacity: var(--ro,0.4);             }
}
.ar-title .ar-star { animation: ar-twinkle 1.5s ease-in-out infinite; }
.ar-title .ar-reflect { transform-box: fill-box; animation: ar-shimmer 6.5s linear infinite; }
/* halt the ambient motion while the tab is hidden — the browser stops painting a
   hidden tab, but this also kills the style-recalc churn of the animations. */
.ar-title.anim-paused .ar-star, .ar-title.anim-paused .ar-reflect { animation-play-state: paused; }
/* reduced-motion → a fully static poster: the turbulence filters are static (no
   SMIL), so disabling these CSS animations leaves no motion at all. */
@media (prefers-reduced-motion: reduce) { .ar-title .ar-star, .ar-title .ar-reflect { animation: none; } }
/* Freeze the heavy reflect-glint layer on devices that can't animate it at 60fps
   (SVG transform/opacity animations repaint on the main thread — no GPU compositor
   layer like HTML gets — and over a high-DPI surface the repaints add up). The cheap
   star twinkle (tiny opacity-only dots) stays. Two triggers:
   - touch devices (phones / touch tablets): frozen pre-emptively so they never
     even start heavy (no probe-window jank);
   - ANY device a runtime FPS probe measures below the bar → adds .ar-lowpower (see
     showTitleScreen). Catches weak GPUs/CPUs of any form factor. */
@media (hover: none) and (pointer: coarse) { .ar-title .ar-reflect { animation: none; } }
.ar-title.ar-lowpower .ar-reflect { animation: none; }
/* title lettering: HTML over the poster, pinned top<->bottom so it re-distributes as
   the hero changes aspect; cqw sizes scale the text with the poster width. */
.ar-title .hero-title { position:absolute; inset:0; pointer-events:none;
  display:flex; flex-direction:column; justify-content:space-between; align-items:center;
  padding:5cqw 3cqw 3.5cqw; font-family:Georgia,'Times New Roman',serif; font-weight:700; text-align:center; }
.ar-title .hero-title .top { display:flex; flex-direction:column; align-items:center; gap:0.4cqw; }
.ar-title .t1 { font-size:7.2cqw; letter-spacing:1.2cqw; line-height:1; color:hsl(43, 62.1%, 88.6%);
  -webkit-text-stroke:0.2cqw hsl(220, 60%, 9.8%); paint-order:stroke; }
.ar-title .t2 { font-size:5.55cqw; letter-spacing:1.39cqw; line-height:1; color:hsl(42, 46.5%, 83.1%);
  -webkit-text-stroke:0.2cqw hsl(220, 60%, 9.8%); paint-order:stroke; }
.ar-title .t3 { font-size:1.67cqw; letter-spacing:0.83cqw; line-height:1; color:hsl(186, 50.6%, 83.3%);
  opacity:0.85; white-space:pre; }
/* continuous parchment panel: buttons + footer share one background & border so the
   footer reads as connected to the buttons, not a separate floating box. The poster
   has a far higher grow priority (100 vs 1), so it soaks up spare height first; only
   once it hits its max aspect does the leftover flow here, extending the panel below
   the buttons (flex-shrink 0 keeps it from collapsing under its content). */
.ar-title .panel { flex:1 0 auto; display:flex; flex-direction:column; justify-content:space-between; box-sizing:border-box;
  background: linear-gradient(180deg,hsl(44, 55.6%, 89.4%),var(--parch) 18%,var(--parch2) 60%);
  border:3px solid var(--wood); border-top:3px solid var(--brass-d); }
.ar-title .menu { padding:28px 18px 28px;
  /* mobile-first: a single stacked column of full-width buttons */
  display:flex; flex-direction:column; gap:11px; flex:0 0 auto; }
/* wider phones / tablets up: two buttons per row, full-width ones span both */
@media (min-width:480px) {
  .ar-title .menu { flex-direction:row; flex-wrap:wrap; gap:11px 14px; }
  .ar-title .menu .ar-btn { flex:1 1 calc(50% - 7px); }
  .ar-title .menu .full { flex:1 1 100%; }
}
.ar-title .ar-btn { font-family:inherit; font-weight:700; font-size:16px; padding:12px 14px; cursor:pointer; color:hsl(28, 55.6%, 10.6%);
  background:linear-gradient(180deg,var(--brass-l),var(--brass) 55%,var(--brass-d)); border:2px solid var(--wood-d); border-radius:4px;
  box-shadow: inset 0 1px 0 hsla(0, 0%, 100%, 0.55), inset 0 -2px 2px hsla(0, 0%, 0%, 0.18), 0 1px 2px hsla(0, 0%, 0%, 0.3);
  text-shadow:0 1px 0 hsla(0, 0%, 100%, 0.35); transition: filter 0.08s; text-align:left; display:flex; align-items:center; gap:9px; }
.ar-title .ar-btn:hover:not(:disabled) { filter:brightness(1.07); }
.ar-title .ar-btn:active:not(:disabled) { box-shadow: inset 0 2px 4px hsla(0, 0%, 0%, 0.35); transform: translateY(1px); }
.ar-title .ar-btn:disabled { opacity:0.45; cursor:default; filter:none; }
.ar-title .ar-btn.primary { color:hsl(90, 100%, 95.7%); background:linear-gradient(180deg,hsl(105, 47.8%, 64.7%),hsl(105, 38.4%, 43.9%) 55%,hsl(105, 41.7%, 29.6%));
  border-color:hsl(110, 40.6%, 25.1%); text-shadow:0 1px 0 hsla(0, 0%, 0%, 0.3); }
.ar-title .ar-btn .ic { display:inline-flex; align-items:center; flex:0 0 auto; }
.ar-title .ar-btn .ic svg { width:21px; height:21px; display:block; }
.ar-title .ar-btn .sub { font-weight:400; font-size:11.5px; font-style:italic; color:hsl(31, 47.6%, 28.4%); margin-left:auto; }
.ar-title .ar-btn.primary .sub { color:hsl(94, 59.3%, 88.4%); }
/* Resume button carries a long info subtitle, so stack it UNDER the label (title +
   subtitle) instead of inline-right — that way the main "Resume Battle" text never
   competes for width and never wraps; only the subtitle wraps if it must. */
.ar-title .ar-btn.ar-resume { align-items:center; }
.ar-title .ar-resume .rlabel { display:flex; flex-direction:column; gap:2px; min-width:0; line-height:1.2; }
.ar-title .ar-resume .sub { margin-left:0; }
.ar-title .footer { position:relative; display:flex; align-items:center; justify-content:space-between; gap:10px;
  font-size:11.5px; color:hsl(31, 47.6%, 28.4%); font-style:italic; padding:6px 16px; flex:0 0 auto; }
/* thin, inset divider above the footer text (replaces the old thick wood border) */
.ar-title .footer::before { content:''; position:absolute; top:0; left:16px; right:16px; height:1px;
  background:hsla(31, 47.6%, 28.4%, 0.4); }
.ar-title .footer .ver { font-style:normal; font-variant-numeric:tabular-nums; opacity:0.85; white-space:nowrap; }
/* Wide desktop (≥1400px): the two sections sit SIDE BY SIDE — poster on the left,
   menu panel on the right — instead of stacked. The poster (preserveAspectRatio:slice)
   fills the tall left box, so the stacked-mode aspect clamps are dropped. The frame
   stays consistent: the hero drops its right border and the panel's left border
   becomes the brass divider seam (was the horizontal seam in stacked mode).
   The panel width is fluid — capped at 480px but narrower when space is tight — and
   the menu is a SINGLE column of full-width buttons (overriding the 2-up grid), so it
   reads as a vertical menu and fits the narrower panel. The menu's auto top/bottom
   margins keep it vertically CENTRED while the footer stays pinned to the bottom of
   the screen (auto margins absorb the free space, so the footer — with none — sinks
   to the bottom). */
@media (min-width:1400px) {
  .ar-title { flex-direction:row; }
  .ar-title .hero { flex:1 1 auto; min-height:0; max-height:none;
    border-bottom:3px solid var(--wood); border-right:none; }
  .ar-title .panel { flex:0 0 clamp(320px, 26%, 480px);
    border-top:3px solid var(--wood); border-left:3px solid var(--brass-d); }
  .ar-title .menu { flex-direction:column; margin:auto 0; }
  .ar-title .menu .ar-btn, .ar-title .menu .full { flex:0 0 auto; width:100%; }
}
`;

/** The moonlit-poster hero, hand-authored as inline SVG. Static (deterministic). */
function posterSvg(): string {
  // viewBox 720×400. A framed teal-night poster: navy sky, a great full moon, a
  // moonlit sea drawn in brick-ish wave courses (homage to the in-game brick
  // water), and a dark-red battleship silhouette on the horizon.
  const moon = `
    <defs>
      <radialGradient id="t-moon" cx="0.5" cy="0.5" r="0.5">
        <stop offset="0" stop-color="hsl(49, 77.1%, 93.1%)"/><stop offset="0.7" stop-color="hsl(49, 58%, 80.4%)"/><stop offset="1" stop-color="hsl(48, 41.9%, 66.3%)"/>
      </radialGradient>
      <radialGradient id="t-glow" cx="0.5" cy="0.5" r="0.5">
        <stop offset="0" stop-color="hsl(184, 40.1%, 73.1%)" stop-opacity="0.55"/><stop offset="1" stop-color="hsl(184, 40.1%, 73.1%)" stop-opacity="0"/>
      </radialGradient>
      <linearGradient id="t-sky" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stop-color="hsl(220, 60%, 9.8%)"/><stop offset="0.6" stop-color="hsl(213, 61.9%, 16.5%)"/><stop offset="1" stop-color="hsl(195, 50.8%, 23.1%)"/>
      </linearGradient>
      <linearGradient id="t-sea" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stop-color="hsl(189, 54.3%, 24.9%)"/><stop offset="1" stop-color="hsl(194, 65.5%, 11.4%)"/>
      </linearGradient>
      <!-- ship body: darker, rusted red-brown — moonlit (top-right) → shadow (bottom-left) -->
      <linearGradient id="t-ship" x1="1" y1="0" x2="0" y2="1">
        <stop offset="0" stop-color="hsl(15, 56.9%, 28.2%)"/>
        <stop offset="0.5" stop-color="hsl(8, 59%, 15.3%)"/>
        <stop offset="1" stop-color="hsl(6, 61.3%, 6.1%)"/>
      </linearGradient>
      <!-- silver gradient for the ornate frame flourishes -->
      <linearGradient id="fg-sv" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stop-color="hsl(210, 37.5%, 96.9%)"/>
        <stop offset="0.5" stop-color="hsl(218, 8.2%, 62%)"/>
        <stop offset="1" stop-color="hsl(216, 17.2%, 88.6%)"/>
      </linearGradient>
      <radialGradient id="t-reflect" cx="0.5" cy="0.28" r="0.62">
        <stop offset="0" stop-color="hsl(186, 57.7%, 89.8%)" stop-opacity="0.22"/>
        <stop offset="0.5" stop-color="hsl(186, 50.6%, 83.3%)" stop-opacity="0.08"/>
        <stop offset="1" stop-color="hsl(186, 50.6%, 83.3%)" stop-opacity="0"/>
      </radialGradient>
      <linearGradient id="t-core" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stop-color="hsl(186, 62.5%, 96.9%)" stop-opacity="0.6"/>
        <stop offset="0.4" stop-color="hsl(186, 51.4%, 92.7%)" stop-opacity="0.28"/>
        <stop offset="1" stop-color="hsl(187, 57.4%, 88%)" stop-opacity="0"/>
      </linearGradient>
      <!-- ripple distortion: fractal noise displaces the source so the moonlight
           reflection warps locally like it's on rippling water (not a rigid sway).
           STATIC (no animated baseFrequency) — an animated feTurbulence regenerates
           Perlin noise across the whole region every frame on the CPU and was the
           single biggest title-screen cost; a fixed warp is computed once + cached.
           The reflection still reads alive via the glints' own CSS drift/flicker. -->
      <filter id="t-ripple" x="-15%" y="-15%" width="130%" height="130%">
        <feTurbulence type="fractalNoise" baseFrequency="0.011 0.045" numOctaves="2" seed="11" result="n"/>
        <feDisplacementMap in="SourceGraphic" in2="n" scale="8" xChannelSelector="R" yChannelSelector="G"/>
      </filter>
      <!-- ship reflection: just a soft blur so the mirrored boat is hazier than the
           boat itself. Plain blur (no turbulence + displacement) — that chain is one
           of the heaviest things to rasterize in the poster, and at the reflection's
           0.15 opacity the rippled warp was imperceptible anyway. The leftward skew on
           the <use> still leans it away from the moon; the blur softens it. -->
      <filter id="t-shipref" x="-25%" y="-20%" width="150%" height="170%">
        <feGaussianBlur stdDeviation="1.3"/>
      </filter>
      <filter id="t-soft" x="-40%" y="-25%" width="180%" height="150%">
        <feGaussianBlur stdDeviation="6"/>
      </filter>
      <filter id="t-glint" x="-60%" y="-300%" width="220%" height="700%">
        <feGaussianBlur stdDeviation="0.6"/>
      </filter>
      <filter id="t-glow2" x="-100%" y="-400%" width="300%" height="900%">
        <feGaussianBlur stdDeviation="2.6"/>
      </filter>
      <clipPath id="t-water"><rect x="0" y="290" width="720" height="110"/></clipPath>
    </defs>`;
  // sky + moon glow + moon with a couple of craters
  const sky = `<rect x="0" y="0" width="720" height="290" fill="url(#t-sky)"/>`
    + `<circle cx="512" cy="120" r="150" fill="url(#t-glow)"/>`
    + `<circle cx="512" cy="120" r="78" fill="url(#t-moon)"/>`
    + `<circle cx="492" cy="100" r="11" fill="hsl(47, 47.6%, 71.6%)" opacity="0.6"/>`
    + `<circle cx="534" cy="138" r="8" fill="hsl(47, 47.6%, 71.6%)" opacity="0.5"/>`
    + `<circle cx="520" cy="92" r="5" fill="hsl(47, 47.6%, 71.6%)" opacity="0.45"/>`;
  // a scatter of stars
  let stars = '';
  let s = 1337;
  const rnd = (): number => ((s = (s * 1103515245 + 12345) & 0x7fffffff) / 0x7fffffff);
  // Animate only ~half the stars (every other one drawn); the rest are static at
  // their base opacity. Twinkle is opacity-only and cheap, but halving the live
  // count still trims per-frame style recalc with no visible loss in the field.
  let starN = 0;
  for (let i = 0; i < 60; i++) {
    const x = Math.round(rnd() * 720);
    const y = Math.round(rnd() * 200);
    if (Math.hypot(x - 512, y - 120) < 95) continue; // keep clear of the moon
    const r = rnd() > 0.85 ? 1.4 : 0.8;
    const op = (0.6 + rnd() * 0.4).toFixed(2);
    // each star runs the multi-blink cycle at its own pace/phase, like fireflies
    // (the cycle holds several blinks, so durations are longer than one blink;
    // negative delay = already mid-cycle, so they don't all start together)
    const dur = (3 + rnd() * 4).toFixed(2);
    const delay = (rnd() * 7).toFixed(2);
    stars += (starN++ % 2 === 0)
      ? `<circle class="ar-star" cx="${x}" cy="${y}" r="${r}" fill="hsl(205, 42.2%, 91.2%)" opacity="${op}" style="--o:${op};animation-duration:${dur}s;animation-delay:-${delay}s"/>`
      : `<circle cx="${x}" cy="${y}" r="${r}" fill="hsl(205, 42.2%, 91.2%)" opacity="${op}"/>`;
  }
  // sea + moonlight reflection column + brick-course wave lines
  const sea = `<rect x="0" y="290" width="720" height="110" fill="url(#t-sea)"/>`;
  // Moonlight reflection like a calm lake (last reference): a bright concentrated
  // spot where the moon meets the waterline, a soft narrow halo fading down, and
  // horizontal ripple-glints that are tiny & dense near the source and grow
  // longer / more spaced toward the foreground. The whole thing is clipped to the
  // water (y>=290) so even the blur can't spill into the sky. The column stays
  // narrow — it widens only modestly with depth, not into a broad fan.
  const colHalf = (t: number): number => 12 + t * 60; // narrow at horizon, fans wide at bottom
  // The diffuse glow under the moon: the column halo + the bright core. These go
  // through the ripple filter so the glow warps locally with the water instead of
  // swaying as one rigid shape.
  const glow = `<ellipse cx="512" cy="334" rx="34" ry="48" fill="url(#t-reflect)" filter="url(#t-soft)"/>`
    + `<path d="M497 290 L527 290 L588 396 L436 396 Z" fill="url(#t-core)" filter="url(#t-soft)"/>`;
  let glints = '';
  const GLINTS = 22;
  for (let i = 0; i < GLINTS; i++) {
    const t = Math.pow(i / (GLINTS - 1), 1.5);       // cluster dense near the source, spread below
    const y = 296 + t * 100;
    const lw = (7 + t * 82) * (0.55 + rnd() * 0.45); // short up top, longer ripples below
    const cx = 512 + (rnd() - 0.5) * colHalf(t);     // small jitter within the narrow column
    const h = (1 + t * 0.9 + rnd() * 0.5).toFixed(1);
    // roughly half the glints are near-white highlights; the brightness fades
    // gently with depth so it's spread along the lines, not pooled at the top
    const bright = rnd() > 0.5;
    const fill = bright ? 'hsl(187, 69.2%, 97.5%)' : 'hsl(189, 50%, 87.5%)';
    const op = (Math.max(0.05, (bright ? 0.85 : 0.5) - t * 0.45) * (0.6 + rnd() * 0.4)).toFixed(2);
    const dur = (2.6 + rnd() * 3).toFixed(2);
    const delay = (rnd() * 6).toFixed(2);
    // ALL glints carry the t-glint blur — without it the sharp rects read as hard
    // dashes; the blur gives the soft, glowy moonlight look. base opacity="${op}" so a
    // FROZEN element (reduced-motion / touch / low-power) shows its real subtle opacity
    // instead of falling back to 1 (hard white). Animated glints re-blur per frame, but
    // t-glint is a tiny 0.6px blur on a tiny rect — cheap; the weak-device freeze + FPS
    // probe cover anything that can't afford it.
    const a = `x="${(cx - lw / 2).toFixed(1)}" y="${y.toFixed(1)}" width="${lw.toFixed(1)}" height="${h}" rx="${(Number(h) / 2).toFixed(1)}" fill="${fill}" opacity="${op}" filter="url(#t-glint)"`;
    glints += (i % 2 === 0)
      ? `<rect class="ar-reflect" ${a} style="--ro:${op};animation-duration:${dur}s;animation-delay:-${delay}s"/>`
      : `<rect ${a}/>`;
  }
  // glow warps with the ripple filter; glints keep their own drift/flicker; all
  // clipped to the water so nothing (blur or displacement) spills into the sky.
  const reflect = `<g clip-path="url(#t-water)"><g filter="url(#t-ripple)">${glow}</g>${glints}</g>`;
  // (The curved brick-course wave lines were removed — the sea is carried entirely by
  // the moonlight reflection glints; the two loops below scatter more of those across
  // the whole water in their place.)

  // Lake glow: the big moon lights the whole surface in soft HORIZONTAL blurry
  // glow patches of varying sizes (not lines, not a flat colour wash) smeared
  // along the ripples. Brightest near the moon's column (x 512) but spread the
  // full width — including the left — with a brightness floor so even far water
  // catches some light. They drift/flicker via ar-reflect so the glow shimmers.
  // the ship sits at viewBox x≈150–450 with its waterline base near y≈315.
  // Suppress moonlight glints only right at that base; below it they're welcome —
  // they read as light in the boat's reflection. NOTE: gate the *append*, never
  // `continue` early — skipping the rnd() calls would reshuffle every other glint.
  const BOAT_L = 150, BOAT_R = 452, BOAT_BASE_Y = 322;
  const clearOfBoat = (x: number, y: number): boolean =>
    x <= BOAT_L || x >= BOAT_R || y >= BOAT_BASE_Y;
  let glitter = '';
  for (let i = 0; i < 84; i++) {
    const x = rnd() * 720;
    const y = 296 + rnd() * 100;
    const depth = (y - 296) / 100;                   // 0 at horizon → 1 in the foreground
    // gentle falloff with a floor — widened so the soft glow spreads across the full
    // water behind the reflection lines (which now reach far past the moon column),
    // not just near it; still brightest under the moon.
    const fade = Math.max(0.16, 1 - Math.abs(x - 512) / 900) * (1 - (y - 296) / 168);
    if (fade <= 0.05) continue;
    const rx = (6 + depth * 30 + rnd() * 26).toFixed(1); // wider toward the foreground
    const ry = (1.6 + rnd() * 4).toFixed(1);         // short — horizontal smears
    const op = (fade * (0.11 + rnd() * 0.16)).toFixed(2);
    rnd(); rnd(); // (was per-glow shimmer dur/delay) — these soft glows are STATIC now:
    // t-glow2 has a 300%×900% filter region, so animating one re-blurs a huge area
    // every frame. Keep the two rnd() draws so later elements' RNG layout is unchanged.
    if (clearOfBoat(x, y)) {                         // suppress only the boat's base; rnd already consumed
      glitter += `<ellipse cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" rx="${rx}" ry="${ry}" fill="hsl(186, 58.7%, 87.6%)" filter="url(#t-glow2)" opacity="${op}"/>`;
    }
  }
  // sharper horizontal glints — the crisp moonlight smears catching individual
  // ripples (lightly blurred via t-glint). These now CARRY THE SEA (the curved wave
  // courses were removed), so there are many of them spread across the full width
  // with a gentle falloff + a higher floor — denser/brighter under the moon, but
  // present everywhere so the whole surface reads as a rippled moonlit sea. They
  // stretch with depth (closer ripples reflect longer streaks); a few are near-white
  // sparkle accents for pop.
  for (let i = 0; i < 110; i++) {
    const x = rnd() * 720;
    const y = 296 + rnd() * 100;
    const depth = (y - 296) / 100;
    const fade = Math.max(0.15, 1 - Math.abs(x - 512) / 950) * (1 - (y - 296) / 162);
    if (fade <= 0.08) continue;
    const lw = (3 + depth * 15 + rnd() * 9).toFixed(1); // longer toward the foreground
    const h = (1 + rnd() * 0.8).toFixed(1);
    const sparkle = rnd() > 0.82;                    // occasional bright accent
    const fill = sparkle ? 'hsl(0, 0%, 100%)' : 'hsl(187, 56.3%, 93.7%)';
    const op = (fade * ((sparkle ? 0.7 : 0.3) + rnd() * 0.4)).toFixed(2);
    const dur = (2.6 + rnd() * 3).toFixed(2);
    const delay = (rnd() * 6).toFixed(2);
    if (clearOfBoat(x, y)) {                         // suppress only the boat's base; rnd already consumed
      const a = `x="${(x - Number(lw) / 2).toFixed(1)}" y="${y.toFixed(1)}" width="${lw}" height="${h}" rx="${(Number(h) / 2).toFixed(1)}" fill="${fill}" opacity="${op}" filter="url(#t-glint)"`;
      glitter += (i % 4 === 0)                        // ~1/4 animated (all blurred for the soft glow); rest static + cached
        ? `<rect class="ar-reflect" ${a} style="--ro:${op};animation-duration:${dur}s;animation-delay:-${delay}s"/>`
        : `<rect ${a}/>`;
    }
  }
  glitter = `<g clip-path="url(#t-water)">${glitter}</g>`;
  // dark-red battleship silhouette riding the horizon (~y 290), lit from the
  // upper-right moon: light top edges, a shadowed waterline, and a faint reflection.
  const HRh = 'hsl(17, 47.4%, 44.7%)';  // moonlit highlight rim (top + right edges — moon is upper-right)
  const HRd = 'hsl(11, 65.9%, 8%)';  // deep shadow (waterline + left/under edges)
  const LIT = 'hsl(40, 100%, 70.8%)';  // warm lamplight (portholes / windows)
  // warm lit portholes along the hull + windows on the superstructure & bridge
  let lights = '';
  for (let px = 40; px <= 264; px += 32) lights += `<circle cx="${px}" cy="68" r="1.7" fill="${LIT}"/>`;
  for (let px = 128; px <= 182; px += 18) lights += `<rect x="${px}" y="47" width="4.5" height="6" rx="1" fill="${LIT}"/>`;
  lights += `<circle cx="104" cy="54" r="1.6" fill="${LIT}"/><circle cx="206" cy="54" r="1.6" fill="${LIT}"/>`;
  const shipBody = `
      <path d="M0 60 L300 60 L286 78 L20 78 Z" fill="url(#t-ship)"/>
      <rect x="120" y="40" width="70" height="20" fill="url(#t-ship)"/>
      <rect x="138" y="22" width="30" height="18" fill="url(#t-ship)"/>
      <rect x="96" y="48" width="16" height="12" fill="url(#t-ship)"/>
      <rect x="198" y="48" width="16" height="12" fill="url(#t-ship)"/>
      <rect x="150" y="2" width="6" height="22" fill="url(#t-ship)"/>
      <rect x="153" y="2" width="20" height="6" fill="url(#t-ship)"/>
      <!-- bow gun: turret + two barrels trained forward (left) -->
      <rect x="12" y="53.4" width="32" height="2.4" rx="1.2" fill="url(#t-ship)"/>
      <rect x="12" y="57" width="32" height="2.4" rx="1.2" fill="url(#t-ship)"/>
      <rect x="38" y="50" width="30" height="11" rx="3.5" fill="url(#t-ship)"/>
      <!-- stern gun: turret + two barrels trained aft (right) -->
      <rect x="256" y="53.4" width="32" height="2.4" rx="1.2" fill="url(#t-ship)"/>
      <rect x="256" y="57" width="32" height="2.4" rx="1.2" fill="url(#t-ship)"/>
      <rect x="232" y="50" width="30" height="11" rx="3.5" fill="url(#t-ship)"/>
      <rect x="20" y="73" width="266" height="5" fill="${HRd}" opacity="0.55"/>
      <rect x="0" y="60" width="300" height="1.6" fill="${HRh}"/>
      <rect x="120" y="40" width="70" height="1.4" fill="${HRh}"/>
      <rect x="138" y="22" width="30" height="1.4" fill="${HRh}"/>
      <rect x="150" y="2" width="20" height="1.4" fill="${HRh}"/>
      <rect x="96" y="48" width="16" height="1.2" fill="${HRh}"/>
      <rect x="198" y="48" width="16" height="1.2" fill="${HRh}"/>
      <rect x="38" y="50" width="30" height="1.4" rx="0.7" fill="${HRh}"/>
      <rect x="232" y="50" width="30" height="1.4" rx="0.7" fill="${HRh}"/>
      <rect x="12" y="53.4" width="32" height="0.9" rx="0.4" fill="${HRh}" opacity="0.8"/>
      <rect x="256" y="53.4" width="32" height="0.9" rx="0.4" fill="${HRh}" opacity="0.8"/>
      <rect x="188" y="40" width="2" height="20" fill="${HRh}" opacity="0.8"/>
      <rect x="166" y="22" width="2" height="18" fill="${HRh}" opacity="0.8"/>
      <rect x="110" y="48" width="2" height="12" fill="${HRh}" opacity="0.7"/>
      <rect x="212" y="48" width="2" height="12" fill="${HRh}" opacity="0.7"/>
      <!-- shadow side: left / under edges (away from the upper-right moon) -->
      <rect x="120" y="41" width="2.5" height="19" fill="${HRd}" opacity="0.5"/>
      <rect x="138" y="23" width="2.5" height="17" fill="${HRd}" opacity="0.5"/>
      <rect x="96" y="49" width="2" height="11" fill="${HRd}" opacity="0.45"/>
      <rect x="198" y="49" width="2" height="11" fill="${HRd}" opacity="0.45"/>
      <rect x="38" y="51" width="2.5" height="10" rx="1" fill="${HRd}" opacity="0.4"/>
      <rect x="232" y="51" width="2.5" height="10" rx="1" fill="${HRd}" opacity="0.4"/>
      <rect x="116" y="58" width="80" height="2.5" fill="${HRd}" opacity="0.4"/>
      ${lights}`;
  // the ship, plus a faint vertically-flipped, compressed copy as its water
  // reflection — run through the same ripple filter as the moon's reflection so
  // it warps/shimmers with the water instead of sitting as a rigid mirror image.
  // reflection = flip+compress (matrix), then a leftward skew that grows with
  // depth (slant matrix, pinned at the waterline y≈306) so it leans away from the
  // upper-right moon. Soft ripple+blur via t-shipref.
  const ship = `<g id="ar-ship" transform="translate(150,228)">${shipBody}</g>`
    + `<use href="#ar-ship" transform="matrix(1 0 -0.26 1 79.56 0) matrix(1 0 0 -0.7 0 520.2)" opacity="0.15" filter="url(#t-shipref)"/>`;
  // The title lettering (ADMIRAL'S / RETURN / NAVAL STRATEGY) is HTML layered
  // over this poster (.hero-title in showTitleScreen + CSS), not baked into the SVG.
  return `<svg class="poster" viewBox="0 0 720 400" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice">`
    + moon + sky + stars + sea + reflect + glitter + ship + `</svg>`;
}

/** One silver scroll-flourish for a frame corner (authored top-left; mirrored
 *  into the other corners with CSS transforms). References the #fg-sv gradient. */
function frameCornerSvg(): string {
  return `<svg viewBox="0 0 62 62" xmlns="http://www.w3.org/2000/svg" fill="none" stroke="url(#fg-sv)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">`
    // main scrolls sweeping down the left edge and along the top edge
    + `<path d="M9 25 q-7 11 1 18 q8 6 11 -3 q2 -8 -6 -8 q-5 0 -3 6"/>`
    + `<path d="M25 9 q11 -7 18 1 q6 8 -3 11 q-8 2 -8 -6 q0 -5 6 -3"/>`
    // secondary curls further along each edge
    + `<path d="M8 44 q-4 9 3 14 q5 3 7 -3" stroke-width="1.5" opacity="0.85"/>`
    + `<path d="M44 8 q9 -4 14 3 q3 5 -3 7" stroke-width="1.5" opacity="0.85"/>`
    // small inner tendrils near the corner node
    + `<path d="M15 15 q-3 6 0 11 M15 15 q6 -3 11 0" stroke-width="1.3" opacity="0.7"/>`
    + `<circle cx="11" cy="11" r="2.8" fill="url(#fg-sv)" stroke="none"/>`
    + `<circle cx="8" cy="40" r="1.5" fill="url(#fg-sv)" stroke="none"/>`
    + `<circle cx="40" cy="8" r="1.5" fill="url(#fg-sv)" stroke="none"/>`
    + `</svg>`;
}

/** DD-MM-YYYY HH:MM for the autosave timestamp shown under "Resume Battle". */
function formatSavedAt(ms: number): string {
  const d = new Date(ms);
  const p = (n: number): string => String(n).padStart(2, '0');
  return `${p(d.getDate())}-${p(d.getMonth() + 1)}-${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** Subtitle for the Resume button, mirroring the in-game heading's "{Size}, {Admiral}"
 *  descriptor: "Quick Battle · Medium, Captain · Turn 3/16 · 24-06-2026 14:52"
 *  (older saves that predate the metadata fall back to the bits they do have). */
function resumeSubtitle(): string {
  const m = savedMeta();
  if (!m) return 'your saved quick battle';
  const parts = ['Quick Battle'];
  if (m.level >= 1) {
    const admiral = DIFFICULTY_NAMES[m.level - 1] ?? '';
    const size = m.size ? `${m.size[0]!.toUpperCase()}${m.size.slice(1)}` : '';
    const desc = size ? `${size}, ${admiral}` : admiral; // "Medium, Captain", just like in game
    if (desc) parts.push(desc);
  }
  parts.push(`Turn ${m.turn}/${m.maxTurn}`);
  if (m.savedAt) parts.push(formatSavedAt(m.savedAt));
  return parts.join(' · ');
}

export interface TitleOpts {
  container: HTMLElement;
  onPlay: () => void;
  onCampaign: () => void;
  onInstant: () => void;
  onTutorial: () => void;
  onSettings: () => void;
  /** resume the autosaved quick battle (only offered when a save exists) */
  onResume?: () => void;
}

export interface TitleHandle {
  play(): void;
  campaign(): void;
  instant(): void;
  tutorial(): void;
  settings(): void;
  destroy(): void;
}

export function showTitleScreen(opts: TitleOpts): TitleHandle {
  if (!document.getElementById('ar-title-style')) {
    const style = document.createElement('style');
    style.id = 'ar-title-style';
    style.textContent = CSS;
    document.head.appendChild(style);
  }

  const resumable = hasSave();
  const wrap = document.createElement('div');
  wrap.className = 'ar-title';
  wrap.id = 'ar-title';
  wrap.innerHTML = `
    <div class="hero">${posterSvg()}<div class="hero-title">
      <div class="top"><div class="t1">ADMIRAL'S</div><div class="t2">R E T U R N</div></div>
      <div class="t3">NAVAL  STRATEGY</div>
    </div><div class="ar-frame"><div class="ar-corner tl">${frameCornerSvg()}</div><div class="ar-corner tr">${frameCornerSvg()}</div><div class="ar-corner bl">${frameCornerSvg()}</div><div class="ar-corner br">${frameCornerSvg()}</div></div></div>
    <div class="panel">
      <div class="menu">
        ${resumable ? `<button class="ar-btn primary full ar-resume" id="ar-t-resume"><span class="ic">${ICON_ANCHOR}</span><span class="rlabel">Resume Battle<span class="sub">${resumeSubtitle()}</span></span></button>` : ''}
        <button class="ar-btn${resumable ? '' : ' primary'}" id="ar-t-play"><span class="ic">${ICON_SWORDS}</span>New Battle</button>
        <button class="ar-btn" id="ar-t-campaign"><span class="ic">${ICON_MAP}</span>Campaign</button>
        <button class="ar-btn" id="ar-t-instant"><span class="ic">${ICON_ZAP}</span>Instant Battle</button>
        <button class="ar-btn" id="ar-t-tutorial"><span class="ic">${ICON_CAP}</span>Tutorial</button>
        <button class="ar-btn" id="ar-t-settings"><span class="ic">${ICON_GEAR}</span>Settings</button>
        <button class="ar-btn" id="ar-t-credits" disabled><span class="ic">${ICON_SCROLL}</span>Credits</button>
      </div>
      <div class="footer"><span>A remake of the classic naval strategy game - Admiral.</span><span class="ver">v${APP_VERSION}</span></div>
    </div>`;
  opts.container.appendChild(wrap);

  // Shrink-to-fit for short viewports: the flex layout fills tall screens and
  // shrinks the poster first, but once even the buttons + footer can't fit, the
  // shared helper scales the whole card down so the page never scrolls.
  const stopFit = fitScreen(wrap);

  // Pause the ambient animation whenever the tab is hidden (tab-switch / minimise).
  // SVGSVGElement.pauseAnimations halts any SMIL too — defensive / forward-compatible.
  const poster = wrap.querySelector<SVGSVGElement>('svg.poster');
  const onVisibility = (): void => {
    const hidden = document.hidden;
    wrap.classList.toggle('anim-paused', hidden);
    if (hidden) poster?.pauseAnimations();
    else poster?.unpauseAnimations();
  };
  document.addEventListener('visibilitychange', onVisibility);
  onVisibility();

  // Adaptive perf guard: sample the STEADY-STATE frame rate with the ambient motion
  // running; if this device can't hold the bar, freeze the heavy wave/reflect layers
  // (the cheap star twinkle stays). Hardware-agnostic — catches weak GPUs/CPUs of any
  // form factor, where CSS media queries can't.
  //   • A 600ms warm-up is SKIPPED first: the heavy static filters (moon glow, blurs)
  //     do their one-time rasterization in the opening frames, and measuring that
  //     startup spike falsely flags healthy machines as slow.
  //   • Frames are only counted while visible — rAF throttles in a hidden tab, which
  //     would otherwise read as a slow device.
  //   • Threshold 50fps over an 800ms window — comfortably below 60, so a healthy
  //     display never trips it; a genuinely struggling device is well under.
  let probeRaf = 0;
  let probeWarmEnd = 0;
  let probeStart = 0;
  let probeFrames = 0;
  const probe = (now: number): void => {
    if (document.hidden) { probeWarmEnd = 0; probeStart = 0; probeFrames = 0; probeRaf = requestAnimationFrame(probe); return; }
    if (!probeWarmEnd) probeWarmEnd = now + 600;
    if (now < probeWarmEnd) { probeRaf = requestAnimationFrame(probe); return; }
    if (!probeStart) probeStart = now;
    probeFrames++;
    const elapsed = now - probeStart;
    if (elapsed < 800) { probeRaf = requestAnimationFrame(probe); return; }
    if ((probeFrames * 1000) / elapsed < 50) wrap.classList.add('ar-lowpower');
    probeRaf = 0;
  };
  probeRaf = requestAnimationFrame(probe);

  // Fade the poster out, then navigate — a soft transition between screens.
  const leave = (fn: () => void): void => {
    wrap.classList.add('leaving');
    window.setTimeout(fn, 230);
  };

  const bind = (id: string, fn: () => void): void =>
    wrap.querySelector(`#${id}`)?.addEventListener('click', () => leave(fn));
  bind('ar-t-play', opts.onPlay);
  bind('ar-t-campaign', opts.onCampaign);
  bind('ar-t-instant', opts.onInstant);
  bind('ar-t-tutorial', opts.onTutorial);
  // Settings opens an overlay ON TOP of the title (it doesn't navigate away), so it
  // must NOT run the leave-fade — otherwise the title fades out behind the overlay
  // and never comes back when it closes. Bind it directly.
  wrap.querySelector('#ar-t-settings')?.addEventListener('click', () => opts.onSettings());
  if (resumable && opts.onResume) bind('ar-t-resume', opts.onResume);

  return {
    play: opts.onPlay,
    campaign: opts.onCampaign,
    instant: opts.onInstant,
    tutorial: opts.onTutorial,
    settings: opts.onSettings,
    destroy: () => {
      stopFit();
      document.removeEventListener('visibilitychange', onVisibility);
      if (probeRaf) cancelAnimationFrame(probeRaf);
      wrap.remove();
    },
  };
}
